import { Box, Button, Flex, Text, useToast, VStack } from '@chakra-ui/react';
import { getDealsApi } from 'api/dealApi';
import { getSalesCohortsApi, getSalesSettingsApi, updateSalesSettingsApi } from 'api/salesApi';
import { currencyFormat, percentageFormat } from 'components/Number';
import OverlaySpinnerWrapper from 'components/OverlaySpinner';
import SelectInput from 'components/inputs/SelectInput';
import {
  COMPARISON_PERIOD_OPTIONS,
  ERROR_CODES,
  TIME_PERIOD,
} from 'constants/salesConstants';
import { BUSINESS_MODEL_TYPE } from 'constants/underwritingConstants';
import { CockpitCustomerContext } from 'context/CockpitCustomerContext';
import { getDefaultSalesAsOfDate } from 'helpers/dateHelper';
import { useMarketingViews } from 'hooks/useMarketingViews';
import { mapRoasThresholdsFromApi } from 'pages/Cockpit/Operations/roasCovenantMonth';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { transformCohorts } from 'transformers/cohortsTransformer';
import DateInputHeader from '../DateInputHeader';
import SalesSettings from '../Sales/components/SalesSettings';
import CacVsMarketingChart from './CacVsMarketingChart';
import MarketingChartNotes from './MarketingChartNotes';
import RoasByMobChart from './RoasByMobChart';
import { buildDealMaxCacOptions } from './dealMaxCacOptions';
import { toForecastCohorts } from './mapCohorts';

const DEFAULT_COHORTS_MONTHS_BACK = 36;

const MarketingPerformance = ({ isPlayground }) => {
  const urlParams = useParams();
  const [searchParams] = useSearchParams();
  const customerId = isPlayground ? searchParams.get('customerId') : urlParams.customerId;
  const toast = useToast();
  const { customer } = useContext(CockpitCustomerContext);
  const { options: marketingViewOptions } = useMarketingViews(customerId);

  const [date, setDate] = useState(getDefaultSalesAsOfDate());
  const [settings, setSettings] = useState(null);
  const [defaultSettings, setDefaultSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [cohortsRaw, setCohortsRaw] = useState(null);
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const isSubscription = customer?.model === BUSINESS_MODEL_TYPE.SUBSCRIPTION;

  useEffect(() => {
    if (!customerId) {
      return undefined;
    }
    let cancelled = false;

    const loadSettings = async () => {
      setLoading(true);
      try {
        const saved = await getSalesSettingsApi({ customerId });
        if (cancelled) {
          return;
        }
        saved.comparisonPeriod = COMPARISON_PERIOD_OPTIONS[0].value;
        setDefaultSettings(saved);
        setSettings({
          ...saved,
          timePeriod: TIME_PERIOD.MONTHLY,
          cohortsMonthsBack: DEFAULT_COHORTS_MONTHS_BACK,
        });
      } catch (error) {
        if (!cancelled) {
          toast({
            title: 'Error',
            description: 'Failed to load sales preferences',
            status: 'error',
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSettings();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  useEffect(() => {
    if (!customerId) {
      return undefined;
    }
    let cancelled = false;
    getDealsApi({ customerId })
      .then((loadedDeals) => {
        if (!cancelled) {
          setDeals(loadedDeals || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDeals([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  useEffect(() => {
    if (!customerId || !settings || !date) {
      return undefined;
    }
    let cancelled = false;

    const loadCohorts = async () => {
      setLoading(true);
      try {
        const raw = await getSalesCohortsApi({
          customerId,
          date,
          timePeriod: settings.timePeriod || TIME_PERIOD.MONTHLY,
          withPrediction: settings.withPrediction,
          currentMonthType: settings.currentMonthType,
          cohortsMonthsBack: settings.cohortsMonthsBack || DEFAULT_COHORTS_MONTHS_BACK,
          marketingViewId: settings.marketingViewId,
        });
        if (!cancelled) {
          setCohortsRaw(raw);
        }
      } catch (error) {
        if (!cancelled) {
          setCohortsRaw(null);
          toast({
            title: 'Error',
            description:
              error?.response?.data?.errorCode === ERROR_CODES.MISSING_CAC
                ? 'Missing CAC in marketing segment'
                : 'Error getting cohorts',
            status: 'error',
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCohorts();
    return () => {
      cancelled = true;
    };
  }, [customerId, date, settings]);

  const cohorts = useMemo(() => {
    if (!cohortsRaw?.rows?.length || !settings) {
      return [];
    }
    return toForecastCohorts(transformCohorts(cohortsRaw, settings).rows);
  }, [cohortsRaw, settings]);

  const cohortIds = cohorts.map((cohort) => cohort.id).join('|');

  useEffect(() => {
    setSelectedIds(cohortIds ? cohortIds.split('|') : []);
  }, [cohortIds]);

  const dealOptions = useMemo(() => buildDealMaxCacOptions(deals), [deals]);

  useEffect(() => {
    setSelectedDealId(dealOptions[0]?.id ?? null);
  }, [dealOptions]);

  const selectedDeal = deals.find((deal) => deal.id === selectedDealId) || null;
  const thresholds = mapRoasThresholdsFromApi(selectedDeal?.roasThresholds).filter(
    (threshold) => threshold.month !== '' && threshold.minRoasPct !== ''
  );

  const saveDefaultSettings = async (newSettings) => {
    setIsSavingSettings(true);
    try {
      await updateSalesSettingsApi({ customerId, data: newSettings });
      setDefaultSettings(newSettings);
      toast({
        title: 'Success',
        description: 'Default preferences saved successfully',
        status: 'success',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save default preferences',
        status: 'error',
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const selectRecentCohorts = (count) => {
    const recent = cohorts.slice(Math.max(cohorts.length - count, 0));
    setSelectedIds(recent.map((cohort) => cohort.id));
  };

  return (
    <OverlaySpinnerWrapper show={loading} isFixed>
      <Box minW="container.lg" maxW="max" pb={20}>
        <DateInputHeader
          date={date}
          onDateChange={setDate}
          showCustomerSelect={isPlayground}
          rightButtonElm={
            settings && (
              <SalesSettings
                date={date}
                loading={isSavingSettings}
                isSubscription={isSubscription}
                initialSettings={settings}
                defaultSettings={defaultSettings}
                onSettingsApply={setSettings}
                onSaveAsDefault={saveDefaultSettings}
                isPlayground={isPlayground}
                marketingViewOptions={marketingViewOptions}
                applyLabel="APPLY PREFERENCES"
                showCreateTearsheet={false}
                showCohortsRange
              />
            )
          }
        />

        <Box mb={8}>
          {customer?.companyName ? (
            <Text fontSize="sm" color="gray.600" mb={2}>
              {customer.companyName}
            </Text>
          ) : null}
          {selectedDeal ? (
            <Text fontSize="sm">
              Deal <b>{selectedDeal.name}</b>
              {selectedDeal.maxCac > 0
                ? ` · Max CAC ${currencyFormat(selectedDeal.maxCac)}`
                : ''}
              {Number(selectedDeal.fundingPercentage) > 0
                ? ` · Funding ${percentageFormat(selectedDeal.fundingPercentage, {
                    maximumFractionDigits: 0,
                  })} of UA`
                : ''}
              {thresholds.length
                ? ` · ROAS covenant ${thresholds
                    .map(
                      (threshold) =>
                        `MOB ${threshold.month} ${percentageFormat(threshold.minRoasPct, {
                          maximumFractionDigits: 0,
                        })}`
                    )
                    .join(' · ')}`
                : ''}
              {Number(selectedDeal.roasCovenantMultiplier) > 0
                ? ` · Covenant basis ${percentageFormat(selectedDeal.roasCovenantMultiplier, {
                    maximumFractionDigits: 0,
                  })}`
                : ''}
              {Number(selectedDeal.secondLevelBreachPct) > 0
                ? ` · 2nd-level breach ${percentageFormat(selectedDeal.secondLevelBreachPct, {
                    maximumFractionDigits: 0,
                  })}`
                : ''}
            </Text>
          ) : (
            <Text fontSize="sm" color="gray.600">
              No deal with a max CAC.
            </Text>
          )}
          {dealOptions.length > 1 && (
            <Box w="320px" mt={3}>
              <SelectInput
                options={dealOptions.map((option) => ({
                  value: option.id,
                  label: `${option.name} · ${currencyFormat(option.maxCac)}`,
                }))}
                value={selectedDealId}
                onChange={setSelectedDealId}
              />
            </Box>
          )}
        </Box>

        <Flex gap={3} wrap="wrap" align="center" mb={8}>
          <Box minW="280px" flex="1">
            <SelectInput
              isMulti
              compactMulti
              compactMultiNoun="cohorts"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              placeholder="Cohorts"
              options={cohorts.map((cohort) => ({
                value: cohort.id,
                label: cohort.label,
              }))}
              value={selectedIds}
              onChange={(ids) => setSelectedIds(ids || [])}
            />
          </Box>
          <Button size="sm" variant="outline" onClick={() => selectRecentCohorts(6)}>
            Last 6
          </Button>
          <Button size="sm" variant="outline" onClick={() => selectRecentCohorts(12)}>
            Last 12
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSelectedIds(cohorts.map((cohort) => cohort.id))}
          >
            All
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSelectedIds([])}>
            Clear
          </Button>
        </Flex>

        <VStack spacing="60px" align="stretch">
          <CacVsMarketingChart
            cohorts={cohorts}
            selectedIds={selectedIds}
            maxCac={Number(selectedDeal?.maxCac) > 0 ? Number(selectedDeal.maxCac) : null}
          />
          <RoasByMobChart cohorts={cohorts} selectedIds={selectedIds} deal={selectedDeal} />
          <MarketingChartNotes />
        </VStack>
      </Box>
    </OverlaySpinnerWrapper>
  );
};

MarketingPerformance.propTypes = {
  isPlayground: PropTypes.bool,
};

export default MarketingPerformance;
